import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCommercialJobNumber, type QuoteState } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconQuotes } from '../icons';
import { display, QUOTE_STATUS } from '../status';
import { money } from '../money';
import { NewQuoteSheet } from '../create/NewQuoteSheet';
import { dayInTz } from '../../lib/format';

export type QuoteRevision = {
  id: string;
  revision: number;
  title: string | null;
  status: string;
  totalCents: number;
  validUntil: string | Date | null;
  convertedJobId?: string | null;
  sentAt?: string | Date | null;
  acceptedAt?: string | Date | null;
  estimatorUserId?: string | null;
  estimatorName?: string | null;
};
export type QuoteFamily = {
  id: string;
  quoteNumber: number | null;
  currentQuoteId: string | null;
  revisionCount?: number;
  revisions: QuoteRevision[];
  workOrder: { id: string; jobNumber: number; status: string } | null;
};
export type QuoteJobGroup = {
  commercialJob: {
    id: string;
    jobNumber: number;
    title: string;
    customerName: string | null;
  };
  families: QuoteFamily[];
  familyPage?: {
    total: number;
    limit: number;
    offset: number;
    nextOffset: number | null;
  };
};

/** The server groups at Job level, so pagination never splits a quote's revisions. */
export function QuoteFamilyGroups({
  groups,
  showJob = true,
  status,
  search,
  ownerUserId,
  pipelineId,
}: {
  groups: QuoteJobGroup[];
  showJob?: boolean;
  status?: QuoteState;
  search?: string;
  ownerUserId?: string;
  pipelineId?: string;
}) {
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <QuoteJobFamilies
          key={JSON.stringify([
            group.commercialJob.id,
            status,
            search,
            ownerUserId,
            pipelineId,
          ])}
          group={group}
          showJob={showJob}
          status={status}
          search={search}
          ownerUserId={ownerUserId}
          pipelineId={pipelineId}
        />
      ))}
    </div>
  );
}

function QuoteJobFamilies({
  group,
  showJob,
  status,
  search,
  ownerUserId,
  pipelineId,
}: {
  group: QuoteJobGroup;
  showJob: boolean;
  status?: QuoteState;
  search?: string;
  ownerUserId?: string;
  pipelineId?: string;
}) {
  const trpc = useTRPC();
  const [offset, setOffset] = useState(0);
  const page = useQuery({
    ...trpc.quote.familyList.queryOptions({
      commercialJobId: group.commercialJob.id,
      status,
      search,
      ownerUserId,
      pipelineId,
      limit: 20,
      offset,
    }),
    enabled: offset > 0,
  });
  const rows = offset === 0 ? group.families : page.data?.rows;
  const total =
    offset === 0 ? (group.familyPage?.total ?? group.families.length) : page.data?.total;
  const nextOffset = offset === 0 ? group.familyPage?.nextOffset : page.data?.nextOffset;
  const state = offset === 0 ? 'ready' : shownState(page, page.data);
  return (
    <section className="space-y-3" aria-label={`Quotes for ${group.commercialJob.title}`}>
      {showJob ? (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
          <Link
            to="/jobs/$jobId"
            params={{ jobId: group.commercialJob.id }}
            className="text-sm font-semibold hover:underline"
          >
            {formatCommercialJobNumber(group.commercialJob.jobNumber)} ·{' '}
            {group.commercialJob.title}
          </Link>
          <span className="text-xs text-muted-foreground">
            {group.commercialJob.customerName || 'Customer not assigned'}
          </span>
        </header>
      ) : null}
      {offset > 0 ? (
        <StaleNotice
          show={refreshFailed(page, page.data) || page.isPaused}
          onRetry={() => void page.refetch()}
        />
      ) : null}
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconQuotes}
          what="these quotes"
          error={page.error}
          onRetry={() => void page.refetch()}
        />
      ) : (
        rows?.map((family) => {
          const current = family.revisions.find(
            (revision) => revision.id === family.currentQuoteId,
          );
          if (!current)
            return (
              <Notice key={family.id} tone="warn">
                The current revision is unavailable. Refresh this Job before proceeding.
              </Notice>
            );
          const revisionCount = family.revisionCount ?? family.revisions.length;
          return (
            <article
              key={family.id}
              className="rounded-[var(--radius)] border border-border bg-surface p-3"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="font-mono">
                  {family.quoteNumber ? `Q-${family.quoteNumber}` : 'Draft quote'}
                </span>
                <span>
                  Separate quote · {revisionCount}{' '}
                  {revisionCount === 1 ? 'revision' : 'revisions'}
                </span>
              </div>
              <QuoteRevisionRow revision={current} />
              {family.workOrder ? (
                <Link
                  className="mt-3 block text-xs font-semibold hover:underline"
                  to="/work-orders/$workOrderId"
                  params={{ workOrderId: family.workOrder.id }}
                >
                  Open confirmed work order #{family.workOrder.jobNumber}
                </Link>
              ) : current.status === 'approved' ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Customer accepted. Office confirmation is still required; no stock or
                  crew is held.
                </p>
              ) : null}
              {revisionCount > 1 ? (
                <QuoteRevisionHistory
                  familyId={family.id}
                  currentQuoteId={family.currentQuoteId}
                  count={revisionCount - 1}
                />
              ) : null}
            </article>
          );
        })
      )}
      {rows?.length === 0 ? (
        <p className="text-sm text-muted-foreground">No quotes linked to this Job yet.</p>
      ) : null}
      {total !== undefined ? (
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>
            {offset + (rows?.length ? 1 : 0)}–{offset + (rows?.length ?? 0)} of {total}{' '}
            separate quotes
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(offset <= 5 ? 0 : Math.max(5, offset - 20))}
          >
            Previous quotes
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={nextOffset == null || page.isFetching}
            onClick={() => {
              if (nextOffset != null) setOffset(nextOffset);
            }}
          >
            More quotes
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function QuoteRevisionHistory({
  familyId,
  currentQuoteId,
  count,
}: {
  familyId: string;
  currentQuoteId: string | null;
  count: number;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const q = useQuery({
    ...trpc.quote.revisionList.queryOptions({
      quoteFamilyId: familyId,
      limit: 20,
      offset,
    }),
    enabled: open,
  });
  const state = shownState(q, q.data);
  return (
    <details
      className="mt-3 border-t border-border pt-2"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="min-h-11 cursor-pointer py-3 text-xs font-semibold">
        Earlier revisions ({count})
      </summary>
      {open ? (
        <div className="space-y-3">
          <StaleNotice
            show={refreshFailed(q, q.data) || q.isPaused}
            onRetry={() => void q.refetch()}
          />
          {state !== 'ready' ? (
            <UnresolvedState
              state={state}
              icon={IconQuotes}
              what="revision history"
              error={q.error}
              onRetry={() => void q.refetch()}
            />
          ) : q.data ? (
            <>
              <ul className="space-y-3">
                {q.data.rows
                  .filter((revision) => revision.id !== currentQuoteId)
                  .map((revision) => (
                    <li key={revision.id}>
                      <QuoteRevisionRow revision={revision} />
                    </li>
                  ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - 20))}
                >
                  Newer revisions
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={q.data.nextOffset === null || q.isFetching}
                  onClick={() => {
                    if (q.data?.nextOffset != null) setOffset(q.data.nextOffset);
                  }}
                >
                  Older revisions
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </details>
  );
}

function QuoteRevisionRow({ revision }: { revision: QuoteRevision }) {
  const trpc = useTRPC();
  const shop = useQuery(trpc.org.info.queryOptions());
  const date = (value: Date | string | null | undefined) =>
    value
      ? shop.data?.timezone
        ? dayInTz(value, shop.data.timezone)
        : `${new Date(value).toISOString()} (UTC)`
      : 'Not recorded';
  const status = display(QUOTE_STATUS, revision.status);
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <Link
          to="/quotes/$quoteId"
          params={{ quoteId: revision.id }}
          className="text-sm font-semibold hover:underline"
        >
          {revision.title || 'Untitled quote'}{' '}
          <span className="block text-xs font-normal text-muted-foreground">
            Revision {revision.revision}
          </span>
        </Link>
        <span className="shrink-0 text-sm font-semibold tabular-nums">
          {money(revision.totalCents)}
        </span>
      </div>
      <StatusBadge tone={status.tone}>
        {revision.status === 'approved' ? 'Accepted' : status.label}
      </StatusBadge>
      {revision.validUntil ? (
        <span className="ml-2 text-xs text-muted-foreground">
          Valid through {new Date(revision.validUntil).toLocaleDateString()}
        </span>
      ) : null}
      <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <div>
          <dt className="inline">Quote estimator: </dt>
          <dd className="inline">
            {revision.estimatorName ??
              (revision.estimatorUserId ? 'Unavailable' : 'Not recorded')}
          </dd>
        </div>
        <div>
          <dt className="inline">Sent: </dt>
          <dd className="inline">{date(revision.sentAt)}</dd>
        </div>
        <div>
          <dt className="inline">Accepted: </dt>
          <dd className="inline">{date(revision.acceptedAt)}</dd>
        </div>
      </dl>
    </div>
  );
}

export function JobQuotesPanel({ commercialJobId }: { commercialJobId: string }) {
  const trpc = useTRPC();
  const [creating, setCreating] = useState(false);
  const q = useQuery(
    trpc.quote.workspaceList.queryOptions({ commercialJobId, limit: 50, offset: 0 }),
  );
  const state = shownState(q, q.data);
  return (
    <SectionCard
      title="Quotes"
      right={
        <Button variant="ghost" size="sm" onClick={() => setCreating(true)}>
          Add separate quote
        </Button>
      }
    >
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconQuotes}
          what="this Job's quotes"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data ? (
        <QuoteFamilyGroups groups={q.data.rows} showJob={false} />
      ) : null}
      {q.data?.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No quotes linked to this Job yet.</p>
      ) : null}
      <NewQuoteSheet
        open={creating}
        onClose={() => setCreating(false)}
        commercialJobId={commercialJobId}
      />
    </SectionCard>
  );
}
